// Master Professional Profile: the user's own record of their facts.
//
// Provenance rule for every fact in this file: `confirmed` means the user has
// explicitly said it is accurate and it may later feed resume generation.
// `needs-review` means a document suggested it and nobody has agreed to it yet.
// `corrected` means text changed after it was last confirmed or imported, so the
// previous confirmation no longer covers it. Nothing is ever promoted on its
// own.

export type FactState = 'needs-review' | 'corrected' | 'confirmed';

export const FACT_STATE_LABEL: Record<FactState, string> = {
  'needs-review': 'From your CV — needs review',
  corrected: 'Edited by you — not yet confirmed',
  confirmed: 'Confirmed by you',
};

/** A single string fact that carries its own provenance. */
export type TrackedValue = {
  value: string;
  state: FactState;
};

/** Common shape of every repeatable profile entry, including contact links. */
export type EntryBase = {
  id: string;
  state: FactState;
  /** Verbatim lines from the source document. Empty string when the user typed
   *  the entry by hand, so there is nothing to compare against. */
  evidence: string;
};

export type ContactLink = EntryBase & {
  label: string;
  url: string;
};

export type ContactField = 'fullName' | 'email' | 'phone' | 'location';

export const CONTACT_FIELDS: readonly ContactField[] = [
  'fullName',
  'email',
  'phone',
  'location',
];

export type ContactFieldLabel = Record<ContactField, string>;

export type ContactInfo = {
  fullName: TrackedValue;
  email: TrackedValue;
  phone: TrackedValue;
  location: TrackedValue;
  links: ContactLink[];
};

/**
 * Dates keep both reading of the source: `raw` is the text as printed in the CV
 * and stays editable for review, while `start`/`end` hold normalized values
 * (`2021`, `2021-03`, or empty when the source did not say).
 */
export type Period = {
  start: string;
  end: string;
  current: boolean;
  raw: string;
};

export type ExperienceEntry = EntryBase & {
  organization: string;
  role: string;
  location: string;
  period: Period;
  responsibilities: string[];
  achievements: string[];
};

export type EducationEntry = EntryBase & {
  institution: string;
  credential: string;
  area: string;
  period: Period;
};

export type SkillEntry = EntryBase & {
  name: string;
};

export type CertificationEntry = EntryBase & {
  name: string;
  issuer: string;
  date: string;
};

export type ProjectEntry = EntryBase & {
  name: string;
  url: string;
  description: string;
  period: Period;
};

/** Maps each repeatable section to its entry type. */
export type ProfileEntries = {
  experience: ExperienceEntry;
  education: EducationEntry;
  skills: SkillEntry;
  certifications: CertificationEntry;
  projects: ProjectEntry;
};

export type EntrySection = keyof ProfileEntries;

export const ENTRY_SECTIONS: readonly EntrySection[] = [
  'experience',
  'education',
  'skills',
  'certifications',
  'projects',
];

export type AnyEntry = ProfileEntries[EntrySection];

export type MasterProfile = {
  contact: ContactInfo;
  summary: TrackedValue;
  experience: ExperienceEntry[];
  education: EducationEntry[];
  skills: SkillEntry[];
  certifications: CertificationEntry[];
  projects: ProjectEntry[];
};

export type EntryPatch<K extends EntrySection> = Partial<
  Omit<ProfileEntries[K], 'id' | 'state' | 'evidence'>
>;

/** One suggested fact read out of a CV. Never a fact until the user accepts it,
 *  and never verified until the user confirms it. */
export type CandidateSection =
  | 'contact'
  | 'summary'
  | EntrySection;

export type CandidatePayload =
  | { kind: 'contact-field'; field: ContactField; value: string }
  | { kind: 'contact-link'; label: string; url: string }
  | { kind: 'summary'; value: string }
  | { kind: 'experience'; entry: EntryDraft<'experience'> }
  | { kind: 'education'; entry: EntryDraft<'education'> }
  | { kind: 'skill'; entry: EntryDraft<'skills'> }
  | { kind: 'certification'; entry: EntryDraft<'certifications'> }
  | { kind: 'project'; entry: EntryDraft<'projects'> };

/** An entry as produced by the parser: fields without identity or provenance.
 *  Distributed over `K` so a union of sections stays a union of entry shapes. */
export type EntryDraft<K extends EntrySection> = K extends EntrySection
  ? Omit<ProfileEntries[K], 'id' | 'state' | 'evidence'>
  : never;

export type CandidateFact = {
  id: string;
  section: CandidateSection;
  /** Short human description of what is being proposed, e.g. a role and employer. */
  title: string;
  payload: CandidatePayload;
  /** Text copied verbatim from the extraction, so the user can compare. */
  evidence: string;
  /** 1-based line numbers in the extracted text the suggestion came from. */
  lines: number[];
  /** What the parser could not read reliably. Empty means nothing was guessed. */
  warnings: string[];
};

export type CandidateDecision = 'pending' | 'accepted' | 'rejected';

/** How the user resolved a clash between a suggestion and an existing fact. */
export type ConflictChoice = 'pending' | 'replace' | 'keep';

export type FactConflict = {
  kind: 'different-value' | 'duplicate-entry';
  existing: string;
  proposed: string;
  /** True when the fact being overwritten is user-confirmed, so the UI must warn
   *  harder about losing verified data. */
  confirmed: boolean;
};

export type ImportSession = {
  document: string;
  /** Full extracted text, kept so review can show the original and re-parse. */
  text: string;
  candidates: CandidateFact[];
  decisions: Record<string, CandidateDecision>;
  conflicts: Record<string, ConflictChoice>;
  /** Payloads the user edited during review, keyed by candidate id. */
  edits: Record<string, CandidatePayload>;
  /** Source lines the parser could not place in any section. */
  unclassified: string[];
  notes: string[];
};
